import { forwardRef, useColorMode } from '@cardboard-ui/react';
import {
  BullhornIcon,
  CalendarIcon,
  FileAltIcon,
  FolderOpenIcon,
  FormIcon,
  GoogleDriveIcon,
  PendingFileIcon,
  UserIcon,
  VaultIcon,
} from 'components/icons';
import { IconProps } from 'components/icons/createIcon';
import { FileIcon } from 'components/icons/FileIcon';
import SpaceIcon from 'components/SpaceIcon';
import React from 'react';
import { useItemHandleFragment$data } from '../../hooks/__generated__/useItemHandleFragment.graphql';
import { BridgeIcon } from 'components/icons/BridgeIcon';
import { isGoogleDriveUrl } from 'utils/isGoogleDriveUrl';
import { useFeatureFlag } from '../../../../utils/sessionProvider';

interface ItemIconProps extends IconProps {
  item: useItemHandleFragment$data;
}

export const ItemIcon = forwardRef<ItemIconProps, 'svg'>(
  ({ item, ...props }, ref) => {
    const { colorMode } = useColorMode();
    const isFeatureGoogleDriveIntegrationEnabled = useFeatureFlag(
      'FEATURE_GOOGLE_DRIVE_INTEGRATION_ITEM_VIEWER',
    );

    switch (item.__typename) {
      case 'Folder':
        return <FolderOpenIcon ref={ref} {...props} />;
      case 'FolderItem':
      case 'AnnouncementAttachment':
      case 'MemberDetailsAttachment':
        switch (item.content?.__typename) {
          case 'Page':
            return <FileAltIcon ref={ref} {...props} />;
          case 'File':
            return <FileIcon mimeType={item.content.mimeType} {...props} />;
          case 'PendingFile':
            return <PendingFileIcon ref={ref} {...props} />;
          case 'Form':
            return <FormIcon ref={ref} {...props} />;
          case 'ExternalBridge': {
            return isFeatureGoogleDriveIntegrationEnabled &&
              isGoogleDriveUrl(item.content?.url || '') ? (
              <GoogleDriveIcon ref={ref} {...props} />
            ) : (
              <BridgeIcon ref={ref} {...props} />
            );
          }
          case 'InternalBridge':
            return <BridgeIcon ref={ref} {...props} />;
        }
        break;
      case 'VaultItem':
        switch (item.content?.__typename) {
          case 'File':
            return <FileIcon mimeType={item.content.mimeType} {...props} />;
          case 'PendingFile':
            return <PendingFileIcon ref={ref} {...props} />;
          default:
            return <FileIcon {...props} />;
        }
      case 'Vault':
        return <VaultIcon ref={ref} {...props} />;
      case 'Event':
        return <CalendarIcon ref={ref} {...props} />;
      case 'Member':
        return <UserIcon ref={ref} {...props} />;
      case 'Space':
        return (
          <SpaceIcon
            ref={ref}
            name={item.name || ''}
            color={item.color && item.color[colorMode]}
            {...props}
          />
        );
      case 'Announcement':
        return <BullhornIcon ref={ref} {...props} />;
    }
    return <></>;
  },
);
